import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';

import { NvTooltip, TextAlign } from 'shared/components/nv-tooltip';
import NvSwitch from 'shared/components/inputs/nv-switch';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse } from 'redux/selectors/course';
import { toggleAiFeedback } from 'redux/actions/submissions';

interface AiFeedbackDropdownItemProps {
  exerciseId: string;
  isCollection?: boolean;
}

// React component for AI Feedback dropdown item with state management
const AiFeedbackDropdownItem = ({ exerciseId, isCollection }: AiFeedbackDropdownItemProps) => {
  const [aiFeedbackEnabled, setAiFeedbackEnabled] = useState(false);
  const dispatch = useAppDispatch();

  const currentCourse = useSelector(getCurrentCourse);
  const exercises = useSelector(state => state?.app?.lecturePage?.exercises);
  const currentLecturePageComponents = useSelector(state => state?.app?.lecturePage?.currentLecturePageComponents);

  const exerciseFromList = exercises?.find(ex => ex?.id === exerciseId);
  const exerciseFromComponents = currentLecturePageComponents?.find(comp => comp?.exercise?.id === exerciseId)?.exercise;
  const currentExercise = exerciseFromList ?? exerciseFromComponents;

  useEffect(() => {
    if (currentExercise) {
      setAiFeedbackEnabled(currentExercise.aiFeedbackEnabled);
    }
  }, [currentExercise]);

  const handleToggle = () => {
    if (isCollection) return;
    setAiFeedbackEnabled(!aiFeedbackEnabled);
    dispatch(toggleAiFeedback({
      catalogId: currentCourse.catalogId,
      exerciseId,
      aiFeedbackEnabled: !aiFeedbackEnabled,
    }));
  };

  return (
    <NvTooltip
      text={t.LECTURE_PAGES.COMPONENTS.DROPDOWN.SETUP_IN_LINKED_LESSON_TOOLTIP()}
      placement='left'
      enabled={isCollection}
      textAlign={TextAlign.LEFT}
    >
      <div
        className={`bs4-dropdown-item d-flex justify-content-center pl-2 align-items-center gap-2 ai-feedback-dropdown-item ${isCollection ? 'text-gray-5' : ''}`}
        onClick={handleToggle}
      >
        <GradientAiIcon size='xss-smallest' icon='gen-ai' className='pr-2' />
        <div className='pr-2'>
          {t.LECTURE_PAGES.COMPONENTS.AI_FEEDBACK_FOR_ADMINS()}
        </div>
        <NvTooltip
          text={aiFeedbackEnabled ? t.LECTURE_PAGES.COMPONENTS.AI_FEEDBACK_FOR_ADMINS_TOOLTIP_OFF() : t.LECTURE_PAGES.COMPONENTS.AI_FEEDBACK_FOR_ADMINS_TOOLTIP()}
          placement='bottom-end'
          enabled={!isCollection}
          textAlign={TextAlign.LEFT}
        >
          <NvSwitch
            name='aiFeedbackEnabled'
            checked={aiFeedbackEnabled}
            disabled={isCollection}
            enabledIcon='gen-ai'
            ariaLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.SWITCH_LABEL()}
          />
        </NvTooltip>
      </div>
    </NvTooltip>
  );
};

export default AiFeedbackDropdownItem;
